import {
  useQuery,
  useQueryClient,
  type UseQueryReturnType,
} from "@tanstack/vue-query";
import type { ISbComponentType, ISbStoryData } from "storyblok-js-client";

import { queries } from "~/queries";
import type {
  ContentTypeMap,
  FolderPaths,
  FolderTypeMap,
} from "~/types/Storyblok";

import type { StoryblokRequestConfigOptions } from "./useStoryblokRequestConfig";

export default <
  TFolderPath extends FolderPaths,
  ContentType extends string = string,
>(
  options: StoryblokRequestConfigOptions<TFolderPath, ContentType>,
) => {
  type ResponseType = ContentType extends keyof ContentTypeMap
    ? ContentTypeMap[ContentType]
    : TFolderPath extends keyof FolderTypeMap
      ? FolderTypeMap[TFolderPath]
      : ISbComponentType<string>;

  const queryClient = useQueryClient();

  const { params, slug, isInPreviewMode } = useStoryblokRequestConfig(options);

  const toggles = useFeatureToggles();

  const result: UseQueryReturnType<ISbStoryData<ResponseType> | null, any> =
    useQuery({
      ...queries.storyblok.story(slug, params, {
        useNitroEndpoint: toggles.value.nitroStoryblokEndpoint,
      }),
      staleTime: Infinity,
      retry: false,
      placeholderData: (previous) => previous,
    });

  const onStoryblokBridgeUpdate = (story: ISbStoryData<ResponseType>) => {
    queryClient.setQueryData(
      queries.storyblok.story(slug, params, {
        useNitroEndpoint: toggles.value.nitroStoryblokEndpoint,
      }).queryKey,
      story,
    );
  };

  const storyId = computed(() => result.data.value?.id);
  watch(
    storyId,
    (newStoryId, oldStoryId) => {
      if (!isInPreviewMode.value) return;

      if (!newStoryId || oldStoryId === newStoryId) return;

      useStoryblokBridge(newStoryId, onStoryblokBridgeUpdate);
    },
    {
      immediate: true,
    },
  );

  return result;
};
